import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import {
  PlayArrow as StartIcon,
  DoubleArrow as ContinueIcon,
  Warning as AttentionIcon,
  CheckCircle as CompleteIcon,
  Inbox as EmptyIcon,
  Lock as ClosedIcon,
} from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import {
  DashboardState,
  DashboardCounts,
  getNextActionContent,
  getPrimaryDashboardAction,
} from '../../Utils/incidentDashboardState';

interface IncidentNextActionPanelProps {
  state: DashboardState;
  counts: DashboardCounts;
  incidentId: string;
  onNavigate: (route: string) => void;
  onCloseIncident?: () => void;
  onRefresh?: () => void;
  onExport?: () => void;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  start: <StartIcon sx={{ fontSize: 28 }} />,
  continue: <ContinueIcon sx={{ fontSize: 28 }} />,
  attention: <AttentionIcon sx={{ fontSize: 28 }} />,
  complete: <CompleteIcon sx={{ fontSize: 28 }} />,
  empty: <EmptyIcon sx={{ fontSize: 28 }} />,
  closed: <ClosedIcon sx={{ fontSize: 28 }} />,
};

const IncidentNextActionPanel: React.FC<IncidentNextActionPanelProps> = ({
  state,
  counts,
  incidentId,
  onNavigate,
  onCloseIncident,
  onRefresh,
  onExport,
}) => {
  const content = getNextActionContent(state, counts);
  const primary = getPrimaryDashboardAction(state, incidentId);

  const handlePrimaryClick = () => {
    if (state === 'complete' && onCloseIncident) {
      onCloseIncident();
    } else if (state === 'emptyRoster' && onRefresh) {
      onRefresh();
    } else if (primary.route) {
      onNavigate(primary.route);
    }
  };

  const secondaryAction = (() => {
    switch (state) {
      case 'notStarted':
        return { label: 'Choose Roster View', onClick: () => onNavigate(`/incidents/${incidentId}/views`) };
      case 'inProgress':
        return counts.notChecked > 0
          ? { label: 'View Not Checked', onClick: () => onNavigate(`/incidents/${incidentId}/headcount?status=notChecked`) }
          : undefined;
      case 'needsAttention':
        return { label: 'Continue Headcount', onClick: () => onNavigate(`/incidents/${incidentId}/headcount`) };
      case 'complete':
        return onExport ? { label: 'Export Report', onClick: onExport } : undefined;
      case 'closed':
        return onExport ? { label: 'Export Report', onClick: onExport } : undefined;
      default:
        return undefined;
    }
  })();

  return (
    <Box
      sx={{
        bgcolor: content.bgColor,
        border: `1.5px solid ${content.borderColor}`,
        borderRadius: '16px',
        px: { xs: 2, sm: 3 },
        py: 2,
        mb: 2.5,
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2,
        flexWrap: 'wrap',
      }}
      role={state === 'needsAttention' ? 'alert' : 'region'}
      aria-label={`${content.title}: ${content.body}`}
    >
      <Box sx={{ color: content.borderColor, display: 'flex', flexShrink: 0 }}>
        {ICON_MAP[content.icon]}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: appColors.text.heading, lineHeight: 1.3 }}>
          {content.title}
        </Typography>
        <Typography variant="body2" sx={{ color: appColors.text.body, mt: 0.25 }}>
          {content.body}
        </Typography>
        {content.secondaryBody && (
          <Typography variant="body2" sx={{ color: appColors.text.body, mt: 0.25 }}>
            {content.secondaryBody}
          </Typography>
        )}
        {content.guidance && (
          <Typography variant="caption" sx={{ color: appColors.text.caption, mt: 0.5, display: 'block' }}>
            {content.guidance}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
        <Button
          variant={primary.variant}
          color={primary.color}
          onClick={handlePrimaryClick}
          aria-label={`${primary.label} for this incident`}
          sx={{ fontWeight: 700, minHeight: 44, whiteSpace: 'nowrap' }}
        >
          {primary.label}
        </Button>
        {secondaryAction && (
          <Button
            variant="outlined"
            onClick={secondaryAction.onClick}
            aria-label={`${secondaryAction.label} for this incident`}
            sx={{ fontWeight: 600, minHeight: 44, whiteSpace: 'nowrap' }}
          >
            {secondaryAction.label}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default IncidentNextActionPanel;
